import { ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReassignResponsibleAgentDialog } from "@/components/ReassignResponsibleAgentDialog";
import { stockApi } from "@/lib/stock.api";
import type { StockMovementWithArticle } from "@/hooks/queries/useStockMovements";
import { MOVEMENT_LABELS } from "../stock.constants";
import { formatDateTime, formatMovementReference } from "../stock-formatters";
import { DetailRow, DetailStat } from "./StockDetailPrimitives";
import { StockMovementBadge, StockMovementIcon } from "./StockBadges";

interface MovementDetailDialogProps {
  movement: StockMovementWithArticle | null;
  canReassign: boolean;
  onOpenChange: (open: boolean) => void;
  onReassigned: () => void;
}

export function MovementDetailDialog({
  movement,
  canReassign,
  onOpenChange,
  onReassigned,
}: MovementDetailDialogProps) {
  return (
    <Dialog open={Boolean(movement)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-2xl">
        {movement && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    Mouvement #{movement.id}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {movement.article?.name ?? `Article #${movement.articleId}`} -{" "}
                    {formatDateTime(movement.createdAt)}
                  </DialogDescription>
                </div>
                <StockMovementBadge type={movement.type} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-3">
              <DetailStat
                label="Quantité"
                value={`${movement.quantity} ${movement.article?.unit ?? ""}`}
              />
              <DetailStat label="Agent" value={`#${movement.agentId}`} />
              <DetailStat
                label="Employé responsable"
                value={`#${movement.responsibleAgentId ?? movement.agentId}`}
              />
              <DetailStat
                label="Sortie forcée"
                value={movement.isNegativeOverride ? "Oui" : "Non"}
                danger={movement.isNegativeOverride}
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="flex items-center gap-2 border-b border-border bg-surface-muted px-4 py-2">
                <StockMovementIcon type={movement.type} />
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Détail du mouvement
                </p>
              </div>
              <table className="w-full text-left">
                <tbody>
                  <DetailRow
                    label="Article"
                    value={movement.article?.name ?? `#${movement.articleId}`}
                  />
                  <DetailRow label="Unité" value={movement.article?.unit ?? "-"} />
                  <DetailRow label="Type" value={MOVEMENT_LABELS[movement.type]} />
                  <DetailRow label="Référence" value={formatMovementReference(movement)} />
                  <DetailRow label="Date" value={formatDateTime(movement.createdAt)} />
                </tbody>
              </table>
            </div>

            {movement.isNegativeOverride && (
              <div className="rounded-lg border border-danger-border bg-danger-bg px-4 py-3">
                <p className="flex items-center gap-2 text-[12px] font-medium text-danger-text">
                  <ShieldAlert size={14} />
                  Ce mouvement contient une sortie forcée avec stock insuffisant.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              {canReassign && (
                <ReassignResponsibleAgentDialog
                  currentResponsibleAgentId={movement.responsibleAgentId ?? movement.agentId}
                  onReassign={(newId, reason) =>
                    stockApi
                      .reassignMovementResponsibleAgent(movement.id, newId, reason)
                      .then(() => onReassigned())
                  }
                />
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
